import { forwardRef, memo, type SelectHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { getAccessibleFormProps, getFocusClasses } from '../../utils/accessibility';
import type { BaseComponentProps } from '../../types';
import type { AccessibleFormProps } from '../../types/accessibility.types';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'>,
    BaseComponentProps {
  /**
   * Label del campo
   */
  label?: string;
  /**
   * Opzioni del select
   */
  options: SelectOption[];
  /**
   * Stato di errore
   */
  error?: boolean;
  /**
   * Messaggio di errore
   */
  errorMessage?: string;
  /**
   * Testo di aiuto
   */
  helpText?: string;
  /**
   * Placeholder (opzione disabled iniziale)
   */
  placeholder?: string;
  /**
   * Dimensione
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Componente Select
 * 
 * Select nativo styled con supporto completo per accessibilità.
 * 
 * @example
 * ```tsx
 * <Select
 *   label="Paese"
 *   options={[
 *     { value: 'it', label: 'Italia' },
 *     { value: 'us', label: 'Stati Uniti' }
 *   ]}
 *   placeholder="Seleziona un paese"
 * />
 * ```
 */
const SelectComponent = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      label,
      options,
      error,
      errorMessage,
      helpText,
      placeholder,
      size = 'md',
      required,
      ...props
    },
    ref
  ) => {
    const { inputProps, labelProps, errorProps, helpProps } = getAccessibleFormProps({
      id: props.id,
      name: props.name,
      required,
      error,
      errorMessage,
      helpText,
      'aria-label': props['aria-label'],
    });

    const sizeClasses = {
      sm: 'text-sm px-3 py-1.5',
      md: 'text-base px-4 py-2',
      lg: 'text-lg px-5 py-3',
    };

    const selectClasses = cn(
      'w-full rounded-md border transition-colors appearance-none',
      'bg-base-100 text-base-content',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'bg-[url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3e%3cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3e%3c/svg%3e")]',
      'bg-[length:1.5em_1.5em] bg-[right_0.5rem_center] bg-no-repeat pr-10',
      sizeClasses[size],
      error && 'border-error focus:border-error focus:ring-error',
      !error && 'border-base-300 focus:border-primary focus:ring-primary',
      getFocusClasses(),
      className
    );

    return (
      <div className="w-full">
        {label && (
          <label
            {...labelProps}
            className="block text-sm font-medium text-base-content mb-1"
          >
            {label}
            {required && <span className="text-error ml-1" aria-label="richiesto">*</span>}
          </label>
        )}
        
        <select
          ref={ref}
          {...inputProps}
          {...props}
          className={selectClasses}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
        
        {error && errorMessage && (
          <p
            {...errorProps}
            className="mt-1 text-sm text-error"
          >
            {errorMessage}
          </p>
        )}
        
        {!error && helpText && (
          <p
            {...helpProps}
            className="mt-1 text-sm text-base-content/60"
          >
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

SelectComponent.displayName = 'Select';

export const Select = memo(SelectComponent);

